import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Briefcase, GraduationCap, Compass, ArrowRight, ArrowLeft, CheckCircle, Tag, Building } from 'lucide-react';
import toast from 'react-hot-toast';
import { userAPI } from '../utils/api';
import authService from '../firebase/authService';

const STEPS = {
    IDENTITY: 1,
    DETAILS: 2,
    SKILLS: 3,
    ORGANIZATION: 4
};

const OnboardingPage = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    useEffect(() => {
        return authService.onAuthStateChange(setUser);
    }, []);

    const [currentStep, setCurrentStep] = useState(STEPS.IDENTITY);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const [formData, setFormData] = useState({
        identity: '',
        university: '',
        degree: '',
        semester: '',
        current_role: '',
        years_of_experience: '',
        industry: '',
        technical_skills: [],
        soft_skills: [],
        organization_code: ''
    });

    const [techInput, setTechInput] = useState('');
    const [softInput, setSoftInput] = useState('');

    // Fetch existing profile if incomplete
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const res = await userAPI.getProfile();
                if (res?.profile) {
                    setFormData(prev => ({ ...prev, ...res.profile }));
                }
            } catch (e) {
                console.error("Could not fetch profile", e);
            } finally {
                setIsLoading(false);
            }
        };
        if (user) {
            fetchProfile();
        } else {
            // Allow them to start filling it out, but they will need to log in to save
            setIsLoading(false);
        }
    }, [user]);

    const handleNext = () => setCurrentStep(prev => Math.min(prev + 1, STEPS.ORGANIZATION));
    const handlePrev = () => setCurrentStep(prev => Math.max(prev - 1, STEPS.IDENTITY));

    const handleIdentitySelect = (identity) => {
        setFormData(prev => ({ ...prev, identity }));
        // Auto advance
        setTimeout(handleNext, 300);
    };

    const handleAddSkill = (e, type) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const val = type === 'tech' ? techInput : softInput;
            if (!val.trim()) return;

            if (type === 'tech' && !formData.technical_skills.includes(val.trim())) {
                setFormData(prev => ({ ...prev, technical_skills: [...prev.technical_skills, val.trim()] }));
                setTechInput('');
            } else if (type === 'soft' && !formData.soft_skills.includes(val.trim())) {
                setFormData(prev => ({ ...prev, soft_skills: [...prev.soft_skills, val.trim()] }));
                setSoftInput('');
            }
        }
    };

    const removeSkill = (skill, type) => {
        if (type === 'tech') {
            setFormData(prev => ({ ...prev, technical_skills: prev.technical_skills.filter(s => s !== skill) }));
        } else {
            setFormData(prev => ({ ...prev, soft_skills: prev.soft_skills.filter(s => s !== skill) }));
        }
    };

    const handleSubmit = async () => {
        if (!user) {
            toast.error("Please sign in to save your profile");
            return;
        }

        setIsSubmitting(true);
        try {
            await userAPI.updateProfile(formData);
            // Sync to localStorage so RoadmapSetupPage can find it
            localStorage.setItem('userProfile', JSON.stringify(formData));
            toast.success("Profile saved successfully!");
            // Send them to dashboard once done
            navigate('/premium-dashboard');
        } catch (err) {
            toast.error("Failed to save profile. Please try again.");
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center">
                <div className="w-8 h-8 rounded-full border-t-2 border-b-2 border-cyan-400 animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0B0F19] text-gray-100 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 font-sans overflow-hidden relative">
            {/* Background Orbs */}
            <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] rounded-full bg-cyan-900/20 blur-[120px] pointer-events-none" />
            <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] rounded-full bg-indigo-900/20 blur-[120px] pointer-events-none" />

            <div className="max-w-3xl w-full mx-auto relative z-10">
                <div className="text-center mb-10">
                    <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4">
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-indigo-400">
                            Build your Career Profile
                        </span>
                    </h1>
                    <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                        Tell us about your background to receive hyper-personalized roadmaps and opportunities.
                    </p>
                </div>

                {/* Progress Tracker */}
                <div className="mb-8 relative flex justify-between items-center max-w-xl mx-auto">
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-gray-800 rounded-full z-0 overflow-hidden">
                        <motion.div
                            className="h-full bg-gradient-to-r from-cyan-500 to-indigo-500"
                            initial={{ width: 0 }}
                            animate={{ width: `${((currentStep - 1) / 3) * 100}%` }}
                            transition={{ duration: 0.5, ease: "easeInOut" }}
                        />
                    </div>
                    {[1, 2, 3, 4].map((step) => (
                        <div
                            key={step}
                            className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-colors duration-300 ${step < currentStep ? 'bg-indigo-500 text-white' :
                                step === currentStep ? 'bg-cyan-500 text-black outline outline-4 outline-cyan-500/30' :
                                    'bg-gray-800 text-gray-400'
                                }`}
                        >
                            {step < currentStep ? <CheckCircle className="w-5 h-5" /> : step}
                        </div>
                    ))}
                </div>

                {/* Form Container */}
                <div className="bg-gray-900/50 backdrop-blur-xl border border-gray-800/50 rounded-2xl shadow-2xl overflow-hidden p-6 md:p-10 min-h-[400px]">
                    <AnimatePresence mode="wait">

                        {/* STEP 1: IDENTITY */}
                        {currentStep === STEPS.IDENTITY && (
                            <motion.div
                                key="step1"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3 }}
                                className="space-y-6"
                            >
                                <h2 className="text-2xl font-semibold mb-6">What best describes you?</h2>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {[
                                        { id: 'student', title: 'Student', icon: <GraduationCap className="w-8 h-8 mb-4 text-cyan-400" />, desc: 'Currently enrolled in a university or bootcamp' },
                                        { id: 'professional', title: 'Working Professional', icon: <Briefcase className="w-8 h-8 mb-4 text-indigo-400" />, desc: 'Currently employed and looking to upskill' },
                                        { id: 'transitioning', title: 'Transitioning Careers', icon: <Compass className="w-8 h-8 mb-4 text-emerald-400" />, desc: 'Looking to switch to a new tech role' }
                                    ].map((item) => (
                                        <button
                                            key={item.id}
                                            onClick={() => handleIdentitySelect(item.id)}
                                            className={`flex flex-col items-center justify-center p-6 rounded-xl border-2 transition-all duration-200 text-center ${formData.identity === item.id
                                                ? 'border-cyan-500 bg-cyan-500/10 shadow-[0_0_20px_rgba(6,182,212,0.2)] scale-105'
                                                : 'border-gray-700 bg-gray-800/50 hover:border-gray-500 hover:bg-gray-800'
                                                }`}
                                        >
                                            {item.icon}
                                            <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                                            <p className="text-sm text-gray-400 leading-tight">{item.desc}</p>
                                        </button>
                                    ))}
                                </div>
                            </motion.div>
                        )}

                        {/* STEP 2: DETAILS */}
                        {currentStep === STEPS.DETAILS && (
                            <motion.div
                                key="step2"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3 }}
                                className="space-y-6"
                            >
                                <h2 className="text-2xl font-semibold mb-6">Let's get some details</h2>

                                {formData.identity === 'student' ? (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400 mb-1">University / College</label>
                                            <input
                                                type="text"
                                                value={formData.university}
                                                onChange={(e) => setFormData({ ...formData, university: e.target.value })}
                                                className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                                                placeholder="e.g. Stanford University"
                                            />
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-400 mb-1">Degree</label>
                                                <input
                                                    type="text"
                                                    value={formData.degree}
                                                    onChange={(e) => setFormData({ ...formData, degree: e.target.value })}
                                                    className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                                                    placeholder="e.g. B.S. Computer Science"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-400 mb-1">Year / Semester</label>
                                                <input
                                                    type="text"
                                                    value={formData.semester}
                                                    onChange={(e) => setFormData({ ...formData, semester: e.target.value })}
                                                    className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                                                    placeholder="e.g. 3rd Year"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400 mb-1">Current Role</label>
                                            <input
                                                type="text"
                                                value={formData.current_role}
                                                onChange={(e) => setFormData({ ...formData, current_role: e.target.value })}
                                                className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                                                placeholder="e.g. Frontend Developer"
                                            />
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-400 mb-1">Years of Experience</label>
                                                <input
                                                    type="number"
                                                    value={formData.years_of_experience}
                                                    onChange={(e) => setFormData({ ...formData, years_of_experience: e.target.value })}
                                                    className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                                                    placeholder="e.g. 2"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-400 mb-1">Industry</label>
                                                <input
                                                    type="text"
                                                    value={formData.industry}
                                                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                                                    className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                                                    placeholder="e.g. Fintech"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        )}

                        {/* STEP 3: SKILLS */}
                        {currentStep === STEPS.SKILLS && (
                            <motion.div
                                key="step3"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3 }}
                                className="space-y-8"
                            >
                                <div>
                                    <h2 className="text-2xl font-semibold mb-2">Build your Skills Inventory</h2>
                                    <p className="text-gray-400 text-sm mb-6">Add specific technologies and tools you currently know to ensure roadmaps don't repeat what you've already mastered.</p>

                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-gray-400 mb-1"><Tag className="w-4 h-4 inline mr-1" /> Technical Skills</label>
                                        <div className="flex flex-wrap gap-2 mb-3">
                                            <AnimatePresence>
                                                {formData.technical_skills.map((skill, idx) => (
                                                    <motion.span
                                                        initial={{ scale: 0.8, opacity: 0 }}
                                                        animate={{ scale: 1, opacity: 1 }}
                                                        exit={{ scale: 0.8, opacity: 0 }}
                                                        key={`tech-${idx}`}
                                                        className="bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 px-3 py-1 rounded-full text-sm font-medium flex items-center cursor-pointer hover:bg-cyan-500/30 transition-colors"
                                                        onClick={() => removeSkill(skill, 'tech')}
                                                    >
                                                        {skill} <span className="ml-2 text-cyan-200 opacity-60">×</span>
                                                    </motion.span>
                                                ))}
                                            </AnimatePresence>
                                        </div>
                                        <input
                                            type="text"
                                            value={techInput}
                                            onChange={(e) => setTechInput(e.target.value)}
                                            onKeyDown={(e) => handleAddSkill(e, 'tech')}
                                            className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                                            placeholder="Type a skill and press Enter (e.g. Python, React)"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-400 mb-1"><Tag className="w-4 h-4 inline mr-1" /> Soft Skills <span className="text-gray-500">(Optional)</span></label>
                                        <div className="flex flex-wrap gap-2 mb-3">
                                            <AnimatePresence>
                                                {formData.soft_skills.map((skill, idx) => (
                                                    <motion.span
                                                        initial={{ scale: 0.8, opacity: 0 }}
                                                        animate={{ scale: 1, opacity: 1 }}
                                                        exit={{ scale: 0.8, opacity: 0 }}
                                                        key={`soft-${idx}`}
                                                        className="bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 px-3 py-1 rounded-full text-sm font-medium flex items-center cursor-pointer hover:bg-indigo-500/30 transition-colors"
                                                        onClick={() => removeSkill(skill, 'soft')}
                                                    >
                                                        {skill} <span className="ml-2 text-indigo-200 opacity-60">×</span>
                                                    </motion.span>
                                                ))}
                                            </AnimatePresence>
                                        </div>
                                        <input
                                            type="text"
                                            value={softInput}
                                            onChange={(e) => setSoftInput(e.target.value)}
                                            onKeyDown={(e) => handleAddSkill(e, 'soft')}
                                            className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                                            placeholder="Type a skill and press Enter (e.g. Leadership, Agile)"
                                        />
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* STEP 4: ORGANIZATION */}
                        {currentStep === STEPS.ORGANIZATION && (
                            <motion.div
                                key="step4"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3 }}
                                className="space-y-6"
                            >
                                <div className="text-center">
                                    <div className="mx-auto w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mb-4">
                                        <Building className="w-8 h-8 text-gray-400" />
                                    </div>
                                    <h2 className="text-2xl font-semibold mb-2">Join an Organization Cohort</h2>
                                    <p className="text-gray-400 text-sm max-w-sm mx-auto mb-8">
                                        If your university or company has purchased enterprise access, enter your invite code here. Otherwise, you can skip this step.
                                    </p>
                                </div>

                                <div className="max-w-md mx-auto">
                                    <label className="block text-sm font-medium text-gray-400 mb-1">Organization Invite Code (Optional)</label>
                                    <input
                                        type="text"
                                        value={formData.organization_code}
                                        onChange={(e) => setFormData({ ...formData, organization_code: e.target.value.toUpperCase() })}
                                        className="w-full bg-gray-800/80 border border-gray-700 rounded-lg px-4 py-4 text-center font-mono text-xl tracking-widest text-cyan-300 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 shadow-inner"
                                        placeholder="e.g. CT-STAN-2026"
                                    />
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Navigation Buttons */}
                <div className="mt-8 flex justify-between items-center">
                    <button
                        onClick={handlePrev}
                        disabled={currentStep === STEPS.IDENTITY}
                        className={`flex items-center px-6 py-3 rounded-xl font-medium transition-all ${currentStep === STEPS.IDENTITY
                            ? 'opacity-0 pointer-events-none'
                            : 'bg-gray-800 text-white hover:bg-gray-700 shadow-md hover:shadow-lg'
                            }`}
                    >
                        <ArrowLeft className="w-5 h-5 mr-2" /> Back
                    </button>

                    {currentStep < STEPS.ORGANIZATION ? (
                        <button
                            onClick={handleNext}
                            disabled={!formData.identity && currentStep === STEPS.IDENTITY}
                            className={`flex items-center px-8 py-3 rounded-xl font-bold transition-all ${(!formData.identity && currentStep === STEPS.IDENTITY)
                                ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                                : 'bg-gradient-to-r from-cyan-500 to-indigo-500 text-white hover:from-cyan-400 hover:to-indigo-400 shadow-[0_0_15px_rgba(6,182,212,0.4)] hover:shadow-[0_0_25px_rgba(6,182,212,0.6)]'
                                }`}
                        >
                            Continue <ArrowRight className="w-5 h-5 ml-2" />
                        </button>
                    ) : (
                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            className="flex items-center px-8 py-3 rounded-xl font-bold bg-gradient-to-r from-emerald-500 to-cyan-500 text-white hover:from-emerald-400 hover:to-cyan-400 shadow-[0_0_20px_rgba(16,185,129,0.4)] hover:shadow-[0_0_30px_rgba(16,185,129,0.6)] transition-all"
                        >
                            {isSubmitting ? (
                                <span className="flex items-center">
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" /> Save Profile
                                </span>
                            ) : 'Complete Profile'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OnboardingPage;
